import { Link } from 'react-router-dom';
import { DocumentSection } from '../components/DocumentSection';
import { SUPPORT_EMAIL } from '../lib/support';
import { heading, lead, textLink } from '../lib/ui';

/**
 * 利用規約（#295）。
 *
 * Google の OAuth の同意画面に、ホームページ・プライバシーポリシーと並べて載せる（Google は任意としているが、
 * UGC を扱うので置く）。2026-10-05 にユーザーの判断で確定版として出した（専門家の確認は経ていない）。
 *
 * ## 書き換えるときに気をつけること
 *
 * - **できないことを、できると書かない。** 違反への対応（非公開・ニックネームを外す・利用停止）は
 *   ADR-031 / ADR-032 / ADR-034 で実装したものだけを書いている
 * - 情報の扱いは**プライバシーポリシーに書き、ここでは繰り返さない**（食い違いの元になる）
 * - 変えるときは民法第 548 条の 4 のとおり、効力が生じる日より前にこのページで知らせる。
 *   **内容を変えたら `UPDATED_AT` を更新する**
 */

/** 最終更新日。**内容を変えたら必ず更新する。** */
const UPDATED_AT = '2026-10-05';

const LICENSED_USES = [
  'サーバーに保存し、複製すること',
  'AI に送って鑑定し、選手の画像を作ること',
  '大きさや形式を変えること',
  '大会・ランキング・ともだちと対戦など本サービスの中と、このサイトやシェアの画像に表示すること',
];

const PROHIBITED = [
  '暴力的・性的・差別的な絵や名前、ほかの人をいやな気持ちにさせる絵や名前を作ること',
  '自分やほかの人の本名・学校名・住所・連絡先などを、選手の名前・ニックネーム・絵に入れること',
  'ほかの人の著作権・商標などの権利を侵害すること（ほかの人の絵を写して送る、マンガやアニメのキャラクターをそのまま描いて送る、など）',
  'ほかの利用者へのいやがらせや、ほかの人へのなりすまし',
  '複数のアカウントを使うなどして、大会やランキングの結果を不正に操作すること',
  '本サービスの不具合を利用すること。アプリやサーバーを改造・解析・攻撃すること。自動化したプログラムで本サービスを使うこと',
  '本サービスを使って商売や宣伝をすること',
  '法令や公の秩序に反すること',
];

const ACTIONS = [
  '選手を、ほかの人から見えなくする',
  'ニックネームやアバターを外す',
  'アカウントの利用を止める',
];

export function Terms() {
  return (
    <div className="space-y-6 pt-8">
      <header className="space-y-3">
        <h1 className={heading}>利用規約</h1>
        <p className={lead}>
          この利用規約（以下「本規約」）は、weBaby合同会社（以下「当方」）が提供する
          drawgon（以下「本サービス」）を使うときのきまりです。本サービスを使う前に読み、同意したうえで使ってください。
          <br />
          最終更新日: {UPDATED_AT}
        </p>
      </header>

      <DocumentSection title="本規約への同意">
        <p className={lead}>本サービスを使うと、本規約に同意したものとみなします。</p>
        <p className="mt-3 text-sm text-text-muted">
          未成年の方は、保護者の方の同意を得てから使ってください。保護者の方は、お子さまといっしょに本規約と
          <Link to="/privacy" className={`${textLink} text-text`}>
            プライバシーポリシー
          </Link>
          を読んでください。未成年の方が本サービスを使った場合は、保護者の方の同意を得たものとみなします。
        </p>
      </DocumentSection>

      <DocumentSection title="アカウント">
        <p className={lead}>アプリを初めて起動したときに、アカウントを作ります。</p>
        <p className="mt-3 text-sm text-text-muted">
          機種変更やアプリの削除に備えるには、アプリの「データ引き継ぎ」を設定してください。設定しないままアプリを削除したり、
          端末をなくしたりすると、選手や戦績に戻れなくなり、当方も元に戻せません。
        </p>
        <p className="mt-3 text-sm text-text-muted">
          アカウントを、ほかの人に譲ったり貸したりしないでください。
        </p>
      </DocumentSection>

      <DocumentSection title="料金">
        <p className={lead}>
          本サービスは無料で使えます。有料の機能を設けるときは、内容と料金をあらかじめお知らせします。通信にかかる料金は、利用者の負担です。
        </p>
      </DocumentSection>

      <DocumentSection title="描いた絵と選手">
        <p className={lead}>
          描いた絵の著作権は、描いた利用者のものです。当方に移ることはありません。
        </p>
        <p className="mt-3 text-sm text-text-muted">
          利用者は当方に、本サービスを提供するために必要な範囲で、描いた絵・選手の名前・ニックネーム・アバターを、次のように無償で使うことを許します。
          この許可は、アカウントを削除するまで続きます。
        </p>
        <ul className="mt-2 space-y-1.5 text-sm text-text-muted">
          {LICENSED_USES.map((use) => (
            <li key={use}>・{use}</li>
          ))}
        </ul>
        <p className="mt-3 text-sm text-text-muted">
          選手の能力値・系統・特性は、AI
          の鑑定と本サービスのルールで決まります。鑑定の結果の理由の説明や、鑑定のやり直しには応じられません。
          対戦の勝ち負けは、AI ではなく、本サービスのルールにもとづいてサーバーで決めます。
        </p>
      </DocumentSection>

      <DocumentSection title="してはいけないこと">
        <ul className="space-y-1.5 text-sm text-text-muted">
          {PROHIBITED.map((item) => (
            <li key={item}>・{item}</li>
          ))}
        </ul>
      </DocumentSection>

      <DocumentSection title="本規約に反したとき">
        <p className={lead}>
          本規約に反する使い方を見つけたとき、または通報を受けて確かめたとき、当方は予告なく次の対応をとることがあります。
          対応の理由はお伝えしないことがあります。
        </p>
        <ul className="mt-2 space-y-1.5 text-sm text-text-muted">
          {ACTIONS.map((action) => (
            <li key={action}>・{action}</li>
          ))}
        </ul>
        <p className="mt-3 text-sm text-text-muted">
          利用を止めたアカウントは、アプリから削除できません。利用の停止についてのご相談や、削除のご希望は、下の問い合わせ先で受け付けます。
        </p>
      </DocumentSection>

      <DocumentSection title="いやなものを見つけたとき">
        <p className={lead}>
          ほかの人の絵や名前に、いやな言葉・見せてはいけないもの・名前や学校名などが入っているのを見つけたら、アプリの「しらせる」で当方に知らせてください。
          「みえなくする」を使うと、その人の選手が見えなくなります。ほかの利用者との間で困ったことがあったときも、下の問い合わせ先でご相談ください。
        </p>
      </DocumentSection>

      <DocumentSection title="本サービスの変更・中断・終了">
        <p className={lead}>
          当方は、本サービスのルール・数値・機能を変えることがあります。ゲームのバランスを整えるため、選手の能力値の扱いや大会の仕組みを変えることもあります。
        </p>
        <p className="mt-3 text-sm text-text-muted">
          保守や障害など、やむを得ない理由で本サービスを止めることがあります。本サービスを終えるときは、できるだけ前もってお知らせします。
          終えたあとは、選手や戦績を使えなくなります。
        </p>
      </DocumentSection>

      <DocumentSection title="当方の責任">
        <p className={lead}>
          当方は、本サービスに不具合が無いこと、いつでも使えること、利用者の目的に合うことを約束するものではありません。
        </p>
        <p className="mt-3 text-sm text-text-muted">
          本サービスに関して利用者に損害が生じた場合、当方は、当方の故意または重大な過失による場合を除き、利用者に現実に生じた通常の損害の範囲でのみ責任を負います。
        </p>
      </DocumentSection>

      <DocumentSection title="アカウントの削除">
        <p className={lead}>
          アプリの「データ引き継ぎ」の画面から、いつでもアカウントを削除できます。削除すると、選手や戦績は元に戻せません。
          情報の扱いは
          <Link to="/privacy" className={`${textLink} text-text`}>
            プライバシーポリシー
          </Link>
          に書いています。
        </p>
      </DocumentSection>

      <DocumentSection title="本規約を変えるとき">
        <p className={lead}>
          当方は、民法第 548 条の 4
          にもとづき本規約を変えることがあります。変えるときは、変えた内容と効力が生じる日を、その日より前にこのページでお知らせします。
          大切な変更は、このサイトやアプリでお知らせします。
        </p>
      </DocumentSection>

      <DocumentSection title="準拠法と裁判所">
        <p className={lead}>
          本規約は、日本の法律に従って解釈します。本サービスに関して争いが生じた場合は、当方の本店所在地を管轄する地方裁判所を、第一審の専属的合意管轄裁判所とします。
        </p>
      </DocumentSection>

      <DocumentSection title="問い合わせ">
        <p className={lead}>
          本規約や本サービスについてのお問い合わせは、
          <a href={`mailto:${SUPPORT_EMAIL}`} className={`${textLink} text-text`}>
            {SUPPORT_EMAIL}
          </a>
          へお送りください（
          <Link to="/support" className={`${textLink} text-text`}>
            おといあわせ
          </Link>
          ）。
        </p>
      </DocumentSection>
    </div>
  );
}
